import { createContext, useContext, ReactNode, PropsWithChildren } from "react";
import useEmblaCarousel from "embla-carousel-react";
import type { EmblaOptionsType, EmblaPluginType } from "embla-carousel";
import clsx from "clsx";

interface CarouselContext {
  viewportRef: (node: HTMLElement | null) => void;
}

const CarouselContext = createContext<CarouselContext | null>(null);

const useCarouselContext = () => {
  const context = useContext(CarouselContext);
  if (!context) {
    throw new Error("SimpleCarousel.Slide must be used within SimpleCarousel");
  }
  return context;
};

interface SimpleCarouselProps {
  children: ReactNode;
  options?: EmblaOptionsType;
  plugins?: EmblaPluginType[];
  className?: string;
}

const SimpleCarouselRoot = ({
  children,
  options = { align: "start", loop: false },
  plugins,
  className,
}: SimpleCarouselProps) => {
  const [viewportRef] = useEmblaCarousel(options, plugins);

  return (
    <CarouselContext.Provider value={{ viewportRef }}>
      <div
        className="relative w-full"
        role="region"
        aria-roledescription="carousel"
      >
        <div ref={viewportRef} className="overflow-hidden touch-pan-y">
          <div className={clsx("flex", className)}>{children}</div>
        </div>
      </div>
    </CarouselContext.Provider>
  );
};

interface SlideProps extends PropsWithChildren {
  className?: string;
}

const Slide = ({ children, className }: SlideProps) => {
  useCarouselContext();

  return (
    <div className={clsx("min-w-0 shrink-0 grow-0 basis-full", className)}>
      {children}
    </div>
  );
};

export const SimpleCarousel = Object.assign(SimpleCarouselRoot, {
  Slide,
});
